"use client";

import { skipToken, useQuery } from "@apollo/client/react";
import Link from "next/link";
import { use } from "react";
import {
  LuBadgeDollarSign,
  LuCrown,
  LuUser as LuUser2,
  LuUsers as LuUsers2,
} from "react-icons/lu";

import DiscoverStatFooter from "@/components/app/discover/discover-stat-footer";
import { FollowUserListsButton } from "@/components/app/lists/follow-user-lists-button";
import { MessageLink } from "@/components/app/messages/message-link";
import { ProfileStat } from "@/components/app/profile/profile-stat";
import { SubscribeButton } from "@/components/app/subscribe-button";
import UserConnectedSocialsList from "@/components/app/user-connected-socials-list";
import { HolderBadge } from "@/components/shared/holder-badge";
import { TriggerLoggedInModalWindow } from "@/components/shared/is-logged-in";
import { StopPropagationWrapper } from "@/components/shared/stop-propagation-wrapper";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { UserContext } from "@/contexts/user-context";
import type { CreatorStat } from "@/graphql/codegen/graphql";
import {
  AccountExtendedByPublicKeyDocument,
  AccountsDocument,
  CreatorStatsDocument,
  TokenBalancesDocument,
} from "@/graphql/codegen/graphql";
import { useUserFollowStatus } from "@/hooks-api/use-user-follow-status";
import { pluralize, shortenLongWord } from "@/utils/helpers";
import { getMarkdownBody } from "@/utils/post-utils";
import { getTotalSubscribersCount } from "@/utils/subscriptions";
import {
  getUserDisplayName,
  getUserName,
  isDesoPublicKey,
} from "@/utils/usernames";

import CopyToClipboard from "./copy-to-clipboard";
import DesoAvatar from "./deso-avatar";
import { Spinner } from "./spinner";
import { UserVerifiedIcon } from "./user-verified-icon";
import { UserWhaleIcon } from "./user-whale-icon";

type AccountSocialCardContentProps = {
  clickable?: boolean;
  open: boolean;
  publicKey?: string;
  username?: string | null;
};

export function AccountSocialCardContent({
  clickable = true,
  open,
  publicKey,
  username,
}: AccountSocialCardContentProps) {
  const { currentUserPublicKey } = use(UserContext);

  const { data: usernameData, loading: loadingUserNameData } = useQuery(
    AccountsDocument,
    open && username
      ? {
          variables: {
            filter: {
              username: {
                equalToInsensitive: username.replace(/^(@|%40)/, ""),
              },
            },
            first: 1,
          },
        }
      : skipToken,
  );

  const { data: user, loading: loadingUser } = useQuery(
    AccountExtendedByPublicKeyDocument,
    open && publicKey && isDesoPublicKey(publicKey)
      ? {
          variables: {
            publicKey,
          },
        }
      : skipToken,
  );

  const account =
    user?.accountByPublicKey || usernameData?.accounts?.nodes?.[0];
  const effectivePublicKey = account?.publicKey ?? publicKey;

  const { data: creatorStatResponse } = useQuery(
    CreatorStatsDocument,
    open && effectivePublicKey
      ? {
          variables: {
            filter: {
              publicKeyBase58Check: {
                equalTo: effectivePublicKey,
              },
            },
          },
        }
      : skipToken,
  );

  const { data: tokenBalancesData } = useQuery(
    TokenBalancesDocument,
    open && effectivePublicKey && currentUserPublicKey
      ? {
          variables: {
            filter: {
              creator: {
                publicKey: {
                  equalTo: currentUserPublicKey,
                },
              },
              isDaoCoin: {
                equalTo: true,
              },
              holder: {
                publicKey: {
                  equalTo: effectivePublicKey,
                },
              },
            },
          },
        }
      : skipToken,
  );

  const { isPublicKeyFollowedByUser } = useUserFollowStatus(effectivePublicKey);

  const creatorStats = creatorStatResponse?.creatorStats?.nodes?.[0] || {
    totalAssetsBalanceUsdCents:
      user?.accountByPublicKey?.totalBalanceUsdCents ?? 0,
    publicKeyBase58Check: effectivePublicKey,
  };

  const accountsCurrentUserTokenBalance =
    tokenBalancesData?.tokenBalances?.nodes?.[0]?.balanceNanos ?? 0;

  if (!publicKey && !username) {
    return (
      <StopPropagationWrapper
        className="
          relative rounded-lg border border-solid border-transparent bg-card
          in-[.drawer]:rounded-none in-[.drawer]:bg-transparent
          md:min-w-[400px]
        "
      >
        <div className="flex w-full flex-col gap-3 p-4">
          <div className="text-sm font-semibold">Account Preview</div>
          <p className="text-sm text-muted">
            Open this preview from a user avatar, name, or card trigger.
          </p>
        </div>
      </StopPropagationWrapper>
    );
  }

  if (loadingUser || loadingUserNameData) {
    return (
      <StopPropagationWrapper
        className="
          relative rounded-lg border border-solid border-transparent bg-card
        "
      >
        <div
          className="
            flex h-[120px] min-w-[400px] items-center justify-center
          "
        >
          <Spinner size={32} className="m-auto" />
        </div>
      </StopPropagationWrapper>
    );
  }

  if (!account) {
    const fallbackHref = username || publicKey;
    const fallbackDisplayName =
      username?.replace(/^(@|%40)/, "") ??
      (publicKey ? getUserDisplayName({ publicKey }) : "Unknown User");
    const fallbackUsername = username
      ? username.startsWith("@")
        ? username
        : `@${username}`
      : publicKey
        ? getUserName({ account: { publicKey } })
        : "";

    return (
      <StopPropagationWrapper
        className="
          relative rounded-lg border border-solid border-transparent bg-card
          in-[.drawer]:rounded-none in-[.drawer]:bg-transparent
          md:min-w-[400px]
        "
      >
        <div
          className="
            flex w-full flex-col items-start gap-4 p-4
            lg:flex-row
          "
        >
          <div className="flex w-full">
            <div className="flex w-full gap-x-2">
              <div className="w-full">
                <header className="mb-2 flex justify-between">
                  <div className="flex w-full gap-x-2">
                    <DesoAvatar
                      clickable={clickable}
                      account={{
                        publicKey,
                        username,
                      }}
                      size={48}
                    />

                    <div className="flex w-full items-center justify-between">
                      <h4
                        className="
                          flex flex-col gap-1 text-sm text-muted-foreground
                        "
                      >
                        {clickable && fallbackHref ? (
                          <Link
                            href={`/${fallbackHref}`}
                            className="
                              font-sans font-semibold
                              hover:underline
                            "
                          >
                            {fallbackDisplayName}
                          </Link>
                        ) : (
                          <span className="font-sans font-semibold">
                            {fallbackDisplayName}
                          </span>
                        )}

                        {fallbackUsername && (
                          <span className="text-muted">{fallbackUsername}</span>
                        )}
                      </h4>
                    </div>
                  </div>
                </header>
                {effectivePublicKey && (
                  <div className="mt-4 mb-2 flex items-center gap-x-2">
                    <Badge className="relative text-xs" variant="outline">
                      <CopyToClipboard text={effectivePublicKey}>
                        <span className="flex items-center font-mono text-xs">
                          {shortenLongWord(effectivePublicKey, 6, 6)}
                        </span>
                      </CopyToClipboard>
                    </Badge>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </StopPropagationWrapper>
    );
  }

  const displayName = getUserDisplayName(account);
  const subscribersCount = getTotalSubscribersCount(account);
  const hasSubscriptionTiers = (account.subscriptionTiers?.totalCount ?? 0) > 0;
  const isMyAccount = currentUserPublicKey === account.publicKey;
  const statusIcons = (
    <>
      <UserVerifiedIcon account={account} width={16} height={16} />
      <UserWhaleIcon
        account={account}
        width={16}
        height={16}
        className="inline-flex translate-y-[-10%]"
      />
    </>
  );

  return (
    <StopPropagationWrapper
      className="
        relative rounded-lg border border-solid border-transparent bg-card
        in-[.drawer]:rounded-none in-[.drawer]:bg-transparent
        md:min-w-[400px]
      "
    >
      <div
        className="
          flex w-full flex-col items-start gap-4 p-4
          lg:flex-row
        "
      >
        <div className="flex w-full">
          <div className="flex w-full gap-x-2">
            <div className="w-full">
              <header className="mb-2 flex justify-between">
                <div className="flex w-full items-center gap-x-2">
                  <DesoAvatar
                    clickable={clickable}
                    account={account}
                    size={48}
                  />

                  <div className="flex w-full items-center justify-between">
                    <h4
                      className="
                        flex flex-col gap-1 text-sm text-muted-foreground
                      "
                    >
                      {clickable ? (
                        <Link
                          href={`/${account.username ?? account.publicKey}`}
                          className="
                            inline-flex items-center gap-1 font-semibold
                            hover:underline
                          "
                        >
                          {displayName}
                          {statusIcons}
                        </Link>
                      ) : (
                        <span
                          className="
                            inline-flex items-center gap-1 font-semibold
                          "
                        >
                          {displayName}
                          {statusIcons}
                        </span>
                      )}

                      <span className="text-muted">
                        {getUserName({ account })}
                      </span>
                    </h4>
                  </div>
                  <UserConnectedSocialsList
                    publicKey={effectivePublicKey!}
                    isMyAccount={isMyAccount}
                  />
                </div>
              </header>

              <div className="mt-4 mb-2 flex items-center gap-x-2">
                <Badge className="relative text-xs" variant="outline">
                  {effectivePublicKey && (
                    <CopyToClipboard text={effectivePublicKey}>
                      <span className="flex items-center font-mono text-xs">
                        {shortenLongWord(effectivePublicKey, 6, 6)}
                      </span>
                    </CopyToClipboard>
                  )}
                </Badge>
                {isPublicKeyFollowedByUser && (
                  <Badge className="relative text-xs" variant="outline">
                    Follows you
                  </Badge>
                )}
                {!!accountsCurrentUserTokenBalance && <HolderBadge />}
              </div>

              <div className="flex items-center gap-x-4 pt-2 text-xs">
                <ProfileStat
                  count={account.followerCounts?.totalFollowers || 0}
                  label={pluralize(
                    account.followerCounts?.totalFollowers || 0,
                    "Follower",
                    {
                      includeCount: false,
                    },
                  )}
                  icon={<LuUsers2 />}
                  loading={loadingUser}
                  className="gap-x-1"
                />

                <ProfileStat
                  count={account.followingCounts?.totalFollowing || 0}
                  label="Following"
                  icon={<LuUser2 />}
                  loading={loadingUser}
                  className="gap-x-1"
                />

                {subscribersCount > 0 && (
                  <ProfileStat
                    count={subscribersCount}
                    label={pluralize(subscribersCount, "Subscriber", {
                      includeCount: false,
                    })}
                    icon={<LuBadgeDollarSign className="size-5" />}
                    loading={loadingUser}
                    className="gap-x-1"
                  />
                )}
              </div>
              <div
                className="
                  mt-4 line-clamp-2 max-w-[400px] text-sm text-muted
                "
              >
                {getMarkdownBody(account.description ?? "")}
              </div>
              {!isMyAccount && effectivePublicKey && (
                <TriggerLoggedInModalWindow>
                  <div className="mt-4 flex items-center gap-2">
                    <FollowUserListsButton
                      userLabel={getUserDisplayName({
                        extraData: account.extraData,
                        publicKey: effectivePublicKey,
                        username: account.username ?? username ?? "",
                        truncateLength: 40,
                      })}
                      userPublicKey={effectivePublicKey}
                      variant="inline"
                    />
                    {hasSubscriptionTiers && (
                      <SubscribeButton
                        creator={account}
                        renderContext="profile"
                        renderTrigger={({ isSubscribed, loading }) => (
                          <Button
                            variant={
                              isSubscribed ? "default" : "outlinePrimary"
                            }
                            className="h-10 shrink-0 gap-2 px-3"
                            loading={loading}
                          >
                            <LuCrown className="size-4 shrink-0" />
                            <span>
                              {isSubscribed ? "Unsubscribe" : "Subscribe"}
                            </span>
                          </Button>
                        )}
                      />
                    )}
                    <MessageLink publicKey={effectivePublicKey} />
                  </div>
                </TriggerLoggedInModalWindow>
              )}
            </div>
          </div>
        </div>
      </div>

      {creatorStats && (
        <div className="border-t border-border-light">
          <DiscoverStatFooter user={creatorStats as CreatorStat} isCompact />
        </div>
      )}
    </StopPropagationWrapper>
  );
}
